import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { cn } from '@/lib/utils'

export interface SettingsSection {
  id: string
  titleKey: string
  descriptionKey: string
  icon: React.ReactNode
  component: React.ComponentType
  badge?: string
}

interface SettingsLayoutProps {
  sections: SettingsSection[]
  defaultSection?: string
}

export function SettingsLayout({ sections, defaultSection }: SettingsLayoutProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { section } = useParams<{ section?: string }>()

  // Determine active section from URL or default
  const activeSection = section || defaultSection || sections[0]?.id

  // Update URL when section is not in URL but should be
  useEffect(() => {
    if (!section && defaultSection) {
      navigate(`/dashboard/settings/${defaultSection}`, { replace: true })
    }
  }, [section, defaultSection, navigate])

  const currentSection = sections.find((s) => s.id === activeSection)
  const CurrentComponent = currentSection?.component

  const handleSectionChange = (sectionId: string) => {
    navigate(`/dashboard/settings/${sectionId}`)
  }

  return (
    <div className="container mx-auto px-4 py-4 lg:py-8 max-w-7xl">
      {/* Page Header */}
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
          {t('navigation.settings')}
        </h1>
        <p className="text-muted-foreground mt-1 lg:mt-2 text-sm lg:text-base">
          {t('settings.description')}
        </p>
      </div>

      {/* Mobile Tabs Navigation */}
      <div className="lg:hidden mb-6">
        <div className="bg-card border border-border rounded-lg p-1">
          <div className="flex overflow-x-auto scrollbar-hide gap-1">
            {sections.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => handleSectionChange(section.id)}
                className={cn(
                  'flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap',
                  activeSection === section.id
                    ? 'bg-primary/10 text-primary border border-primary/20'
                    : 'text-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                <span className="flex-shrink-0 w-4 h-4">{section.icon}</span>
                <span className="font-medium">{t(section.titleKey)}</span>
                {section.badge && (
                  <span className="flex-shrink-0 bg-primary/20 text-primary text-xs px-1.5 py-0.5 rounded-full">
                    {section.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:flex gap-8">
        {/* Desktop Sidebar Navigation */}
        <aside className="w-64 flex-shrink-0">
          <div className="bg-card border border-border rounded-lg p-2">
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => handleSectionChange(section.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-md text-sm font-medium transition-colors',
                    activeSection === section.id
                      ? 'bg-primary/10 text-primary border border-primary/20'
                      : 'text-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <span className="flex-shrink-0">{section.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{t(section.titleKey)}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {t(section.descriptionKey)}
                    </div>
                  </div>
                  {section.badge && (
                    <span className="flex-shrink-0 bg-primary/20 text-primary text-xs px-2 py-0.5 rounded-full">
                      {section.badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Quick Settings Preview - Desktop Only */}
          <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-dashed border-muted-foreground/20">
            <div className="text-xs text-muted-foreground text-center">
              {t('settings.quickActions')}
            </div>
            <div className="text-xs text-muted-foreground/70 text-center mt-1">
              {t('settings.comingSoon')}
            </div>
          </div>
        </aside>

        {/* Desktop Main Content */}
        <main className="flex-1 min-w-0">
          {CurrentComponent ? (
            <div className="bg-card border border-border rounded-lg">
              <CurrentComponent />
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <p className="text-muted-foreground">{t('settings.sectionNotFound')}</p>
            </div>
          )}
        </main>
      </div>

      {/* Mobile Main Content */}
      <div className="lg:hidden">
        {CurrentComponent ? (
          <div className="bg-card border border-border rounded-lg">
            <CurrentComponent />
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg p-6 text-center">
            <p className="text-muted-foreground text-sm">{t('settings.sectionNotFound')}</p>
          </div>
        )}
      </div>
    </div>
  )
}
